import type { SupportedLocale } from "staticConfiguration";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import LinkIcon from "@mui/icons-material/Link";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { TFunction } from "core/entities";
import { useToaster } from "core/hooks/useToaster";
import { viewTransition } from "core/utils/viewTransition";
import type { UseFieldArrayReturn, useForm } from "react-hook-form";
import { ReactHookArrayTabs } from "../ReactHookFormComponents/ReactHookArrayTabs";
import { ReactHookTextField } from "../ReactHookFormComponents/ReactHookTextField";
import {
  MAXIMUM_QUICK_LINKS,
  type QuickLinkWidgetFormValues,
} from "./QuickLinkDefs";
import { getCustomLinkDisplayName } from "./SelectedQuickLinksList";

export function QuickLinkCustomLinkForm({
  formMethods,
  fieldArray,
  locale,
  localeOptions,
  t,
  maxSelections = MAXIMUM_QUICK_LINKS,
}: {
  formMethods: ReturnType<typeof useForm<QuickLinkWidgetFormValues>>;
  fieldArray: UseFieldArrayReturn<
    QuickLinkWidgetFormValues,
    "selectedQuickLinks"
  >;
  locale: string;
  localeOptions: { label: string; value: string }[];
  t: TFunction;
  maxSelections?: number;
}) {
  const { fields, append, remove } = fieldArray;
  const { toast } = useToaster();
  const maxLinksMessage = t("validation.maxLinks", {
    defaultValue: "You can select up to {{max}} quick links.",
    max: maxSelections,
  });

  const customLinkEntries = fields
    .map((field, index) => ({ field, index }))
    .filter(({ field }) => field.type === "CustomLink");

  function handleRemoveCustomLink(fieldIndex: number) {
    const currentValues = formMethods.getValues("selectedQuickLinks");
    const quickLinkId = currentValues[fieldIndex]?.id;
    viewTransition(() => {
      remove(fieldIndex);
      if (quickLinkId) {
        const currentOrder = formMethods.getValues("quickLinkOrder");
        formMethods.setValue(
          "quickLinkOrder",
          currentOrder.filter((orderId) => orderId !== quickLinkId)
        );
      }
    });
  }

  function handleAddCustomLink() {
    if (fields.length >= maxSelections) {
      toast({ message: maxLinksMessage, severity: "warning" });
      return;
    }
    const newId = crypto.randomUUID();
    viewTransition(() => {
      append({
        id: newId,
        type: "CustomLink",
        customLink: {
          currentLocaleTab: locale as SupportedLocale,
          translations: [{ locale, name: "", url: "" }],
        },
      });
    });
  }

  return (
    <Stack gap={2}>
      {customLinkEntries.map(({ field, index }) => {
        if (field.type !== "CustomLink" || !field.customLink) {
          return null;
        }
        return (
          <Paper key={field.id} variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <LinkIcon fontSize="small" color="action" />
                <Typography variant="subtitle2">
                  {getCustomLinkDisplayName(field.customLink, locale) ||
                    t("unnamedLink", "Unnamed link")}
                </Typography>
              </Stack>
              <Button
                size="small"
                color="warning"
                onClick={() => handleRemoveCustomLink(index)}
              >
                <CancelIcon fontSize="small" />
              </Button>
            </Stack>
            <Divider />
            <ReactHookArrayTabs
              variant="standard"
              control={formMethods.control}
              currentTabValueName={
                `selectedQuickLinks.${index}.customLink.currentLocaleTab` as any
              }
              localeOptions={localeOptions}
              name={
                `selectedQuickLinks.${index}.customLink.translations` as any
              }
              getNewLocaleObject={(loc) => ({
                locale: loc,
                name: "",
                url: "",
              })}
              translationFieldLocaleKey={"locale" as any}
              render={(trIndex) => (
                <Stack gap={1}>
                  <ReactHookTextField
                    fullWidth
                    required
                    control={formMethods.control}
                    textFieldProps={{ size: "small" }}
                    name={
                      `selectedQuickLinks.${index}.customLink.translations.${trIndex}.name` as any
                    }
                    label={t("customLinkName", "Name")}
                  />
                  <ReactHookTextField
                    fullWidth
                    required
                    textFieldProps={{ size: "small" }}
                    control={formMethods.control}
                    name={
                      `selectedQuickLinks.${index}.customLink.translations.${trIndex}.url` as any
                    }
                    label={t("customLinkUrl", "URL")}
                  />
                </Stack>
              )}
            />
          </Paper>
        );
      })}
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddCustomLink}
      >
        {t("addCustomLink", "Add Link")}
      </Button>
    </Stack>
  );
}
